import { format, getDaysInMonth, parseISO } from "date-fns";
import { Icon } from "semantic-ui-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  PolarAngleAxis,
  RadialBar,
  RadialBarChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { colors } from "../../../style";
import ChartCard from "./ChartCard";
import ChartTooltip from "./ChartTooltip";
import { axisProps, chart, formatNumber } from "./theme";

const STATUS = {
  ok: { color: "#18a19c", icon: "check circle", text: "Within allowance" },
  warning: {
    color: "#fab219",
    icon: "warning circle",
    text: "Approaching limit",
  },
  over: { color: "#e66767", icon: "warning sign", text: "Over limit" },
};

/**
 * Admin only: minutes used this billing month against the customer's pool,
 * with a straight-line projection to month end.
 */
function MinutesUsageCard({ usage, monthly, style }) {
  if (!usage?.limit_minutes) return null;

  const pct = Math.round((usage.used_minutes / usage.limit_minutes) * 100);
  const status = usage.over_limit
    ? STATUS.over
    : pct >= 80
      ? STATUS.warning
      : STATUS.ok;

  let running = 0;
  const cumulative = monthly.map((d) => {
    running += d.minutes;
    return { date: d.date, minutes: running };
  });
  const today = monthly.length
    ? parseISO(monthly[monthly.length - 1].date)
    : new Date();
  const niceMax =
    Math.ceil((Math.max(usage.limit_minutes, running) * 1.1) / 1000) * 1000;
  const projected = Math.round(
    (running / today.getDate()) * getDaysInMonth(today),
  );

  return (
    <ChartCard
      title="Sidekick minutes"
      subtitle={`${format(today, "MMMM yyyy")} billing period, whole organisation`}
      height={260}
      style={style}
      table={{
        columns: ["Date", "Minutes used (cumulative)"],
        rows: cumulative.map((d) => [
          format(parseISO(d.date), "d MMM"),
          d.minutes,
        ]),
      }}
    >
      <div style={{ display: "flex", gap: "20px", height: "100%" }}>
        <div
          style={{
            width: "190px",
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ position: "relative", height: "170px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart
                data={[{ value: Math.min(pct, 100) }]}
                innerRadius="78%"
                outerRadius="100%"
                startAngle={220}
                endAngle={-40}
              >
                <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                <RadialBar
                  dataKey="value"
                  cornerRadius={8}
                  fill={status.color}
                  background={{ fill: "rgba(255,255,255,0.07)" }}
                  isAnimationActive={false}
                />
              </RadialBarChart>
            </ResponsiveContainer>
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  color: colors.text,
                  fontSize: "28px",
                  fontWeight: 600,
                }}
              >
                {pct}%
              </div>
              <div style={{ color: colors.textLight, fontSize: "12px" }}>
                of allowance
              </div>
            </div>
          </div>
          <div
            style={{
              textAlign: "center",
              color: colors.textMuted,
              fontSize: "13px",
            }}
          >
            {formatNumber(usage.used_minutes)} /{" "}
            {formatNumber(usage.limit_minutes)} min
          </div>
          <div
            style={{
              textAlign: "center",
              color: status.color,
              fontSize: "12.5px",
              marginTop: "4px",
            }}
          >
            <Icon name={status.icon} />
            {status.text}
          </div>
        </div>

        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              color: colors.textMuted,
              fontSize: "12.5px",
              marginBottom: "6px",
            }}
          >
            Projected by month end:{" "}
            <strong
              style={{
                color:
                  projected > usage.limit_minutes
                    ? STATUS.over.color
                    : colors.text,
              }}
            >
              {formatNumber(projected)} min
            </strong>
          </div>
          <div style={{ flex: 1, minHeight: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={cumulative}
                margin={{ top: 8, right: 8, bottom: 0, left: -6 }}
              >
                <defs>
                  <linearGradient id="minutes-fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#18a19c" stopOpacity={0.4} />
                    <stop
                      offset="100%"
                      stopColor="#18a19c"
                      stopOpacity={0.04}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke={chart.grid} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => format(parseISO(d), "d MMM")}
                  minTickGap={28}
                  {...axisProps}
                />
                <YAxis
                  domain={[0, niceMax]}
                  tickCount={5}
                  tickFormatter={(v) =>
                    v >= 1000 ? `${Math.round(v / 100) / 10}k` : v
                  }
                  {...axisProps}
                  axisLine={false}
                />
                <ReferenceLine
                  y={usage.limit_minutes}
                  stroke={STATUS.over.color}
                  strokeOpacity={0.7}
                  label={{
                    value: "Allowance",
                    position: "insideTopLeft",
                    fill: STATUS.over.color,
                    fontSize: 11,
                  }}
                />
                <Tooltip
                  cursor={{ stroke: chart.axis }}
                  content={
                    <ChartTooltip
                      title={(label) => format(parseISO(label), "EEE d MMM")}
                      rows={(payload) => [
                        {
                          label: "Used so far",
                          value: `${formatNumber(payload[0].value)} min`,
                          color: "#18a19c",
                        },
                      ]}
                    />
                  }
                />
                <Area
                  type="monotone"
                  dataKey="minutes"
                  stroke="#18a19c"
                  strokeWidth={2}
                  fill="url(#minutes-fill)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </ChartCard>
  );
}

export default MinutesUsageCard;
